import { Seeder } from '../seeder'
import { bsConstants } from '../../module.constants'
import { LiveSeedArcs, SeedArc, SeedArcReview, SeedArcs } from './interfaces'
import { BookSeeder, SeedBook } from '../book'
import { ArcTeamSeeder, SeedArcTeam } from '../arc-team'
import { dateAddDays, dateMinusDays, newDate, startOfDay } from '../../utils'
import { UserSeeder } from '../user'

const { ...FLAGS } = bsConstants.ARCS.REVIEWS.FLAGS

const userSeeder = new UserSeeder()
const seedUsers = { ...userSeeder.getLiveDataObject(), ...userSeeder.getTestDataObject() }

const bookSeeder = new BookSeeder()
const seedBooks = { ...bookSeeder.getLiveDataObject(), ...bookSeeder.getTestDataObject() }

const arcTeamSeeder = new ArcTeamSeeder()
const seedArcTeams = { ...arcTeamSeeder.getLiveDataObject(), ...arcTeamSeeder.getTestDataObject() }

const longLithuanianReview = [
  'Nuo pirmųjų puslapių ši knyga mane įtraukė ne triukšmingu veiksmu, o labai tiksliu emociniu tonu. Autorė iš karto sukuria jausmą, kad veikėjai gyvena tikrame, kvėpuojančiame pasaulyje, kuriame kiekvienas sprendimas turi kainą, o kiekviena tyla tarp dialogų slepia daugiau nei ilgi paaiškinimai. Man labai patiko, kad istorija nebandė skubėti vien tam, kad kuo greičiau pasiektų didžiuosius siužeto posūkius. Vietoje to ji leido santykiams bręsti natūraliai, o įtampai kilti iš charakterių pasirinkimų, vidinių baimių ir to, ko jie vieni kitiems nepasako.',
  'Didžiausią įspūdį paliko pagrindinis veikėjas, kuris parašytas ne kaip tobulas herojus, o kaip žmogus, nuolat svyruojantis tarp pareigos, kaltės ir noro pagaliau gyventi dėl savęs. Jo abejonės atrodė labai žmogiškos, todėl net tada, kai jis klydo, norėjosi jį suprasti, o ne smerkti. Ne mažiau stiprus buvo ir antraeilis veikėjų sluoksnis. Dažnai tokio tipo istorijose šalutiniai veikėjai būna skirti tik tam, kad pastumtų siužetą, bet čia jie turi savo balsą, savo motyvus ir savo emocinį svorį. Kai kurie jų pasirodo trumpam, tačiau vis tiek palieka aiškų pėdsaką.',
  'Rašymo stilius labai sklandus ir vizualus. Aprašymai pakankamai sodrūs, kad matyčiau aplinką, bet niekada netapo sunkūs ar perdėtai puošnūs. Ypač patiko tai, kaip autorė kuria nuotaiką per detales: trumpą gestą, ne iki galo užbaigtą sakinį, pakitusį kambario garsą ar beveik nepastebimą kūno reakciją. Tokios smulkmenos ne tik pagyvina scenas, bet ir sukuria stiprų artumo jausmą. Dialogai taip pat skambėjo natūraliai. Veikėjai kalba skirtingai, todėl lengva pajusti jų charakterį, socialinę padėtį ir emocinę būseną net tada, kai tekstas tiesiogiai to neišaiškina.',
  'Siužeto struktūra išlaiko dėmesį beveik visą laiką. Knygoje netrūksta įtampos, tačiau ji kyla ne vien iš išorinių pavojų, bet ir iš to, kad skaitytojas nuolat jaučia artėjančių pasekmių svorį. Man labai patiko, kad keli siužeto vingiai buvo užuominomis ruošiami gerokai anksčiau, todėl atėjus svarbiems momentams jie atrodė pelnyti, o ne ištraukti vien tam, kad nustebintų. Kartu istorija sugeba išlaikyti ir jautrumą. Kai kurie tylūs, intymūs skyriai man paliko dar didesnį įspūdį nei dramatiškiausios scenos, nes juose veikėjai pagaliau leidžia sau būti pažeidžiami.',
  'Jeigu reikėtų išskirti tai, kas man kliuvo, paminėčiau tik kelis smulkesnius dalykus. Vidurinėje dalyje buvo pora epizodų, kurie galėjo būti šiek tiek trumpesni, nes tuo metu pasakojimas trumpam prarado ankstesnį ritmą. Taip pat vieno antagonisto motyvacija pradžioje atrodė miglota, nors vėliau ji paaiškinama kur kas geriau. Vis dėlto šie trūkumai bendro įspūdžio beveik nesumenkino, nes emocinė istorijos ašis išliko stipri nuo pradžios iki pabaigos. O finalas, mano nuomone, buvo labai gerai subalansuotas: pakankamai užbaigtas, kad suteiktų pasitenkinimą, bet palikęs erdvės ilgesniam apmąstymui.',
  'Labiausiai vertinu tai, kad ši knyga nebando būti vien tik „įtempta“ ar vien tik „jautri“. Ji sugeba būti abu dalykus vienu metu. Joje yra ir įsimenančių scenų, ir prasmingų pokalbių, ir emocinių susidūrimų, kurie atrodo tikri, nes kyla iš veikėjų istorijos, o ne iš autoriaus noro dirbtinai sustiprinti dramą. Perskaitęs jaučiau ne tik smalsumą dėl tolimesnio pasaulio likimo, bet ir tą retą melancholišką jausmą, kuris atsiranda tik po stiprios istorijos, kai dar kurį laiką gyveni jos nuotaika. Tikrai rekomenduočiau šią knygą skaitytojams, kurie mėgsta charakteriais grįstą, atmosferišką ir emociškai brandų pasakojimą.'
].join('\n\n')

export class ArcSeeder extends Seeder<SeedArcs, {}, SeedArc> {
  type = 'arc'
  // 318, not 300: CappedPublisherArcs [sc-29038] and the DelayedAmazon* ARCs [sc-29117] come on top of the
  // random ARCs rather than out of them
  maxModelCount = 318
  processedFakeArcs = 0

  base = {
    discoverableType: bsConstants.ARCS.DISCOVERABLE_TYPE.PUBLIC,
    participationType: bsConstants.ARCS.PARTICIPATION_TYPE.PUBLIC,
    participationPreventionType: bsConstants.ARCS.PARTICIPATION_PREVENTION_TYPE.OUTSTANDING,
    siteWideParticipationPreventionType: bsConstants.ARCS.SITE_WIDE_PARTICIPATION_PREVENTION_TYPE.NONE,
    copyCountType: bsConstants.ARCS.COPY_COUNT_TYPE.ALL,
    seriesLimitType: bsConstants.ARCS.SERIES_LIMIT_TYPE.ANYONE,
    neverEndingPrivate: false,
    reviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS,
    requiredReviewCount: 2,
    stopDownloadDate: dateAddDays(30),
    dueDate: dateAddDays(30),
    liveDate: dateAddDays(14),
    reviewDays: 10, // for never ending arcs
    arcType: bsConstants.ARCS.TYPE.EBOOK
  }

  baseReviewDto = {
    claimId: -1,
    userId: -1,
    disclaimer: 'Disclaimer ...',
    title: 'Title'
  }

  baseSiteDto = {
    userId: -1,
    reviewId: -1,
    link: 'www.google.com'
  }

  getLiveDataObject (): LiveSeedArcs {
    return {
      DeletedArc: {
        ...this.base,
        id: 1,
        specialInformation: '[Deleted ARC]',
        userId: seedUsers.DeletedUser.id,
        bookId: seedBooks.DeletedBook.id,
        arcTeamId: seedArcTeams.DeletedTeam.id
      }
    }
  }

  getTestDataObject (): SeedArcs {
    if (process.env.BUILD_MODE === 'master') {
      return {} as unknown as SeedArcs
    }

    const testData = {
      // Alliance is the first ARC as it means stan.rizzo will have a complete review and automatically
      // be upgraded to a "Value Reviewer" gamification level. Not doing this would cause all other claims
      // to trigger a "tooManyArcsDue" error as newReviewer can only have one due ARC.
      Annihilation: {
        ...this.base,
        id: this.getNextId(),
        liveDate: dateAddDays(0),
        bookId: seedBooks.Annihilation.id,
        userId: seedArcTeams.DrewKarpyshynFans.userId,
        arcTeamId: seedArcTeams.DrewKarpyshynFans.id,
        specialInformation: 'Annihilation Arc',
        reviewFlags: FLAGS.AMAZON,
        optionalReviewFlags: FLAGS.BARNES | FLAGS.KOBO,
        requiredReviewCount: 2,
        userClaims: [
          {
            userId: seedUsers.StanRizzo.id,
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 4,
                review: 'Annihilation was great!',
                privateFeedback: 'Some Annihilation private feedback'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.AMAZON,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                },
                {
                  ...this.baseSiteDto,
                  site: FLAGS.KOBO,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                }
              ]
            }
          },
          {
            userId: seedUsers.HanSolo.id
          },
          {
            userId: seedUsers.MaraJade.id,
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 4,
                review: 'Annihilation was great again!',
                privateFeedback: 'Some Annihilation private feedback from Mara'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.AMAZON,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                },
                {
                  ...this.baseSiteDto,
                  site: FLAGS.KOBO,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                }
              ]
            }
          }
        ]
      },
      Allegiance_NeverEnding10Day: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.Allegiance.id,
        userId: seedArcTeams.TimothyZahnFans.userId,
        arcTeamId: seedArcTeams.TimothyZahnFans.id,
        specialInformation: 'Allegiance Arc',
        reviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS | FLAGS.GOOGLE | FLAGS.BOOK_BUB,
        requiredReviewCount: 1
      },
      ChoicesOfOne: {
        ...this.base,
        id: this.getNextId(),
        liveDate: dateAddDays(0),
        bookId: seedBooks.ChoicesOfOne.id,
        userId: seedArcTeams.TimothyZahnFans.userId,
        arcTeamId: seedArcTeams.TimothyZahnFans.id,
        specialInformation: 'ChoicesOfOne Arc',
        userClaims: [
          {
            userId: seedUsers.StanRizzo.id,
            createdDate: dateMinusDays(14)
          },
          {
            userId: seedUsers.ClaimNonMemberArcDownloadsLocked.id,
            skipEligibleCheck: true
          }
        ]
      },
      DarkForceRising: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.DarkForceRising.id,
        userId: seedArcTeams.TimothyZahnFans.userId,
        arcTeamId: seedArcTeams.TimothyZahnFans.id,
        specialInformation: 'DarkForceRising Arc',
        reviewFlags: FLAGS.BOOK_BUB | FLAGS.AMAZON | FLAGS.ITUNES
      },
      Darksaber: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.DarkSaber.id,
        userId: seedArcTeams.TeamForTheLightSide.userId,
        arcTeamId: seedArcTeams.TeamForTheLightSide.id,
        specialInformation: 'Darksaber Arc',
        liveDate: dateMinusDays(14), // today - 14 days
        vipExclusiveAccessDays: 4,
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.ReviewerNoName.id
          }
        ]
      },
      Deceived: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.Deceived.id,
        userId: seedArcTeams.TeamForTheDarkSide.userId,
        arcTeamId: seedArcTeams.TeamForTheDarkSide.id,
        specialInformation: 'Deceived Arc'
      },
      HeirToTheEmpire: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.HeirToTheEmpire.id,
        userId: seedArcTeams.TimothyZahnFans.userId,
        arcTeamId: seedArcTeams.TimothyZahnFans.id,
        specialInformation: 'HeirToTheEmpire Arc'
      },
      LostTribeOfTheSith: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.LostTribeOfTheSith.id,
        userId: seedArcTeams.TeamForTheDarkSide.userId,
        arcTeamId: seedArcTeams.TeamForTheDarkSide.id,
        specialInformation: 'LostTribeOfTheSith Arc'
      },
      OutboundFlight: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.OutboundFlight.id,
        userId: seedArcTeams.TimothyZahnFans.userId,
        arcTeamId: seedArcTeams.TimothyZahnFans.id,
        specialInformation: 'OutboundFlight Arc'
      },
      PathOfDestruction: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.PathOfDestruction.id,
        userId: seedArcTeams.DrewKarpyshynFans.userId,
        arcTeamId: seedArcTeams.DrewKarpyshynFans.id,
        specialInformation: 'PathOfDestruction Arc'
      },
      RuleOfTwo: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.RuleOfTwo.id,
        userId: seedArcTeams.DrewKarpyshynFans.userId,
        arcTeamId: seedArcTeams.DrewKarpyshynFans.id,
        specialInformation: 'RuleOfTwo Arc'
      },
      TheLastCommand: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.TheLastCommand.id,
        userId: seedArcTeams.TimothyZahnFans.userId,
        arcTeamId: seedArcTeams.TimothyZahnFans.id,
        specialInformation: 'TheLastCommand Arc',
        seriesLimitType: bsConstants.ARCS.SERIES_LIMIT_TYPE.PREVIOUS_REVIEWERS
      },
      GrowingAuthorArc1: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.GrowingAuthorBook1.id,
        userId: seedArcTeams.GrowingAuthorTeam.userId,
        arcTeamId: seedArcTeams.GrowingAuthorTeam.id,
        specialInformation: 'Growing Author ARC',
        liveDate: dateMinusDays(14),
        stopDownloadDate: dateAddDays(46),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      ProAuthorArc1: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.ProAuthorBook1.id,
        userId: seedArcTeams.ProAuthorTeam.userId,
        arcTeamId: seedArcTeams.ProAuthorTeam.id,
        specialInformation: 'Pro Author ARC 1',
        liveDate: dateAddDays(46),
        stopDownloadDate: dateAddDays(60),
        dueDate: dateAddDays(14),
        reviewDays: void 0
      },
      ProAuthorArc2: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.ProAuthorBook1.id,
        userId: seedArcTeams.ProAuthorTeam.userId,
        arcTeamId: seedArcTeams.ProAuthorTeam.id,
        specialInformation: 'Pro Author ARC 2',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(14),
        stopDownloadDate: dateAddDays(46),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      ProAuthorArc3: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.ProAuthorBook1.id,
        userId: seedArcTeams.ProAuthorTeam.userId,
        arcTeamId: seedArcTeams.ProAuthorTeam.id,
        specialInformation: 'Pro Author ARC 3',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(7),
        stopDownloadDate: dateAddDays(53),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      ProAuthorArc4: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.ProAuthorBook1.id,
        userId: seedArcTeams.ProAuthorTeam.userId,
        arcTeamId: seedArcTeams.ProAuthorTeam.id,
        specialInformation: 'Pro Author ARC 4',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateAddDays(0),
        stopDownloadDate: dateAddDays(60),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true,
      },
      BestSellerAuthorArc1: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BestSellerAuthorBook1.id,
        userId: seedArcTeams.BestSellerAuthorTeam.userId,
        arcTeamId: seedArcTeams.BestSellerAuthorTeam.id,
        specialInformation: 'BestSeller Author ARC 1',
        liveDate: dateAddDays(46),
        stopDownloadDate: dateAddDays(60),
        dueDate: dateAddDays(67),
        reviewDays: void 0
      },
      BestSellerAuthorArc2: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BestSellerAuthorBook1.id,
        userId: seedArcTeams.BestSellerAuthorTeam.userId,
        arcTeamId: seedArcTeams.BestSellerAuthorTeam.id,
        specialInformation: 'BestSeller Author ARC 2',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(14),
        stopDownloadDate: dateAddDays(46),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      BestSellerAuthorArc3: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BestSellerAuthorBook1.id,
        userId: seedArcTeams.BestSellerAuthorTeam.userId,
        arcTeamId: seedArcTeams.BestSellerAuthorTeam.id,
        specialInformation: 'BestSeller Author ARC 3',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(7),
        stopDownloadDate: dateAddDays(53),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      BestSellerAuthorArc4: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BestSellerAuthorBook2.id,
        userId: seedArcTeams.BestSellerAuthorTeam.userId,
        arcTeamId: seedArcTeams.BestSellerAuthorTeam.id,
        specialInformation: 'BestSeller Author ARC 4',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(7),
        stopDownloadDate: dateAddDays(53),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      BestSellerAuthorArc5: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BestSellerAuthorBook1.id,
        userId: seedArcTeams.BestSellerAuthorTeam.userId,
        arcTeamId: seedArcTeams.BestSellerAuthorTeam.id,
        specialInformation: 'BestSeller Author ARC 5',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateAddDays(0),
        stopDownloadDate: dateAddDays(60),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      BestSellerAuthorArc6: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BestSellerAuthorBook2.id,
        userId: seedArcTeams.BestSellerAuthorTeam.userId,
        arcTeamId: seedArcTeams.BestSellerAuthorTeam.id,
        specialInformation: 'BestSeller Author ARC 6',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        liveDate: dateAddDays(0),
        stopDownloadDate: dateAddDays(60),
        dueDate: dateAddDays(60),
        reviewDays: void 0,
        skipAudioBookCreation: true
      },
      PublisherArc1: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.PublisherBook1.id,
        userId: seedArcTeams.PublisherTeam.userId,
        arcTeamId: seedArcTeams.PublisherTeam.id,
        specialInformation: 'Publisher Author ARC 1'
      },
      ReviewDueSoonArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.ReviewDueSoon.id,
        userId: seedArcTeams.ReviewDueSoonTeam.userId,
        arcTeamId: seedArcTeams.ReviewDueSoonTeam.id,
        specialInformation: 'DarkForceRising Arc',
        reviewFlags: FLAGS.BOOK_BUB,
        requiredReviewCount: 1,
        liveDate: newDate(), // Live today
        reviewDays: 1, // Review is due in 1 day.
        userClaims: [
          {
            userId: seedUsers.HasReviewDueSoon.id
          }]
      },
      FinishedArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.FinishedArcBook.id,
        userId: seedArcTeams.PlaceholderTeamOne.userId,
        arcTeamId: seedArcTeams.PlaceholderTeamOne.id,
        specialInformation: 'Finished ARC',
        reviewFlags: FLAGS.BOOK_BUB,
        liveDate: dateMinusDays(90),
        stopDownloadDate: dateMinusDays(30),
        dueDate: dateMinusDays(25),
        reviewDays: 1, // Review is due in 1 day.
        neverEndingPrivate: false,
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.UserAlreadyClaimedBookBefore.id,
            createdDate: dateMinusDays(25),
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 5,
                review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
                privateFeedback: 'Some private feedback'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.BOOK_BUB,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                }
              ]
            }
          }
        ]
      },
      DraftArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.DraftArcBook.id,
        userId: seedArcTeams.PlaceholderTeamTwo.userId,
        arcTeamId: void 0 // will be marked as draft
      },
      FutureArc: {
        ...this.genericArc(this.getNextId(), seedBooks.FutureArc),
        liveDate: dateAddDays(21),
        reviewDays: 10, // Review is due in 1 day.
        neverEndingPrivate: false,
        stopDownloadDate: dateAddDays(60),
        dueDate: dateAddDays(67),
      },
      NoCopiesLeft: {
        ...this.genericArc(this.getNextId(), seedBooks.NoCopiesLeft),
        copyCountType: bsConstants.ARCS.COPY_COUNT_TYPE.LIMITED,
        copyCount: 1,
        userClaims: [{
          userId: seedUsers.MaraJade.id,
          createdDate: dateMinusDays(14)
        }] // 1 claim, 1 copy count - should mean no copies left
      },
      BlockedFromTeam: {
        ...this.genericArc(this.getNextId(), seedBooks.BlockedFromTeam, seedArcTeams.StanBlocked)
      },
      ReviewedBookBefore: {
        ...this.genericArc(this.getNextId(), seedBooks.ReviewedBookBefore),
        userClaims: [
          {
            userId: seedUsers.StanRizzo.id,
            createdDate: dateMinusDays(14),
            review: this.genericApprovedReview()
          }
        ]
      },
      NotReviewedBefore: {
        ...this.genericArc(this.getNextId(), seedBooks.NotReviewedBefore),
        participationPreventionType: bsConstants.ARCS.PARTICIPATION_PREVENTION_TYPE.NONE
      },
      HasCopiesLeft: {
        ...this.genericArc(this.getNextId(), seedBooks.HasCopiesLeft),
        copyCount: 10,
        userClaims: [{
          userId: seedUsers.MaraJade.id,
          createdDate: dateMinusDays(14)
        }] // 1 claim, 1 copy count - should mean no copies left
      },
      AudienceGeneral: {
        ...this.genericArc(this.getNextId(), seedBooks.AudienceGeneral)
      },
      AudienceTeen: {
        ...this.genericArc(this.getNextId(), seedBooks.AudienceTeen)
      },
      AudienceMature: {
        ...this.genericArc(this.getNextId(), seedBooks.AudienceMature)
      },
      AudienceExplicit: {
        ...this.genericArc(this.getNextId(), seedBooks.AudienceExplicit)
      },
      StoryShort: {
        ...this.genericArc(this.getNextId(), seedBooks.StoryShort)
      },
      StoryNovella: {
        ...this.genericArc(this.getNextId(), seedBooks.StoryNovella)
      },
      StoryNovel: {
        ...this.genericArc(this.getNextId(), seedBooks.StoryNovel)
      },
      BookNotOnBooksproutBefore: {
        ...this.genericArc(this.getNextId(), seedBooks.BookNotOnBooksproutBefore),
        participationPreventionType: bsConstants.ARCS.PARTICIPATION_PREVENTION_TYPE.NONE
      },
      BookHasRunBeforePreviousRun: {
        ...this.genericArc(this.getNextId(), seedBooks.BookHasRunBefore),
        liveDate: dateMinusDays(60),
        stopDownloadDate: dateMinusDays(46),
        dueDate: dateMinusDays(30), // Should mean this arc has ended
        userClaims: [{
          userId: seedUsers.MaraJade.id,
          createdDate: dateMinusDays(14)
        }]
      },
      BookHasRunBefore: {
        ...this.genericArc(this.getNextId(), seedBooks.BookHasRunBefore)
      },
      FollowingAuthor: {
        ...this.genericArc(this.getNextId(), seedBooks.FollowingAuthor)
      },
      PartOfArcTeam: {
        ...this.genericArc(this.getNextId(), seedBooks.PartOfArcTeam, seedArcTeams.StanRegular)
      },
      HadPreviousPublic: {
        ...this.genericArc(this.getNextId(), seedBooks.BookHasRunBefore)
      },
      WithPastReleaseDate: {
        ...this.genericArc(this.getNextId(), seedBooks.WithPastReleaseDate)
      },
      ExclusiveToTopReviewers: {
        ...this.genericArc(this.getNextId(), seedBooks.ExclusiveToTopReviewers),
        participationType: bsConstants.ARCS.PARTICIPATION_TYPE.TOP_REVIEWERS
      },
      ComplicatedSites: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplicatedSites),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.SMASH_WORDS |
          FLAGS.KOBO |
          FLAGS.BOOK_BUB,
        optionalReviewFlags:
          FLAGS.GOOD_READS |
          FLAGS.GOOGLE,
        requiredReviewCount: 5
      },
      Complete: {
        ...this.genericArc(this.getNextId(), seedBooks.Complete),
        stopDownloadDate: dateMinusDays(1),
        dueDate: dateMinusDays(1),
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(30),
          review: this.genericApprovedReview()
        }]
      },
      Cancelled: {
        ...this.genericArc(this.getNextId(), seedBooks.Cancelled),
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.PERSONAL,
          cancellationReason: 'Stan test cancel'
        }]
      },
      PendingPublishing: {
        ...this.genericArc(this.getNextId(), seedBooks.PendingPublishing),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE |
          FLAGS.BOOK_BUB,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      PendingPublishingWithOptionalStores: {
        ...this.genericArc(this.getNextId(), seedBooks.PendingPublishingWithOptionalStores),
        reviewFlags:
        FLAGS.AMAZON,
        optionalReviewFlags:
          FLAGS.GOOGLE |
          FLAGS.BOOK_BUB,
        requiredReviewCount: 2,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 4,
              review: 'A 4 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      Active: {
        ...this.genericArc(this.getNextId(), seedBooks.Active),
        participationPreventionType: bsConstants.ARCS.PARTICIPATION_PREVENTION_TYPE.NONE,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7)
        }]
      },
      Due: {
        ...this.genericArc(this.getNextId(), seedBooks.Due),
        stopDownloadDate: dateMinusDays(1),
        dueDate: newDate(), // today
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE,
        optionalReviewFlags:
          FLAGS.GOOD_READS |
          FLAGS.ITUNES,
        requiredReviewCount: 3,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 4,
              review: 'A 4 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.ITUNES,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      DueOneDelayedOtherAvailable: {
        ...this.genericArc(this.getNextId(), seedBooks.DueOneDelayedOtherAvailable),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              }
            ]
          }
        }]
      },
      Delayed: {
        ...this.genericArc(this.getNextId(), seedBooks.Delayed),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS,
        requiredReviewCount: 2,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.SMASH_WORDS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      OneDelayedOneWaitingLinkOneReady: {
        ...this.genericArc(this.getNextId(), seedBooks.OneDelayedOneWaitingLinkOneReady),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS |
          FLAGS.BOOK_BUB,
        requiredReviewCount: 3,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 5,
              review: 'A 5 star review from Your Arcs', // keep constant
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.SMASH_WORDS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              }
            ]
          }
        }]
      },
      Private: {
        ...this.genericArc(this.getNextId(), seedBooks.Private),
        neverEndingPrivate: true,
        discoverableType: bsConstants.ARCS.DISCOVERABLE_TYPE.TEAM_ONLY,
        participationType: bsConstants.ARCS.PARTICIPATION_TYPE.ON_TEAM
      },
      DueOnGeneralTeam: {
        ...this.genericArc(this.getNextId(), seedBooks.Private),
        liveDate: dateMinusDays(30),
        stopDownloadDate: dateMinusDays(16),
        reviewDays: 1, // Review is due in 1 day.
        neverEndingPrivate: false,
        dueDate: dateMinusDays(15),
        userClaims: [{
          userId: seedUsers.ClaimOutstandingTeamReviewNotVIP.id,
          createdDate: dateMinusDays(21)
        }, {
          userId: seedUsers.ClaimNonMemberTooManyDue.id,
          createdDate: dateMinusDays(20),
          skipEligibleCheck: true
        }]
      },
      AllSitesWithLinks: {
        ...this.genericArc(this.getNextId(), seedBooks.AllSitesWithLinks),
        reviewFlags:
          FLAGS.AMAZON,
        requiredReviewCount: 5,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS |
          FLAGS.KOBO |
          FLAGS.BOOK_BUB |
          FLAGS.GOOD_READS |
          FLAGS.BARNES |
          FLAGS.GOOGLE |
          FLAGS.ITUNES,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(14),
          review: this.genericApprovedReview()
        }]
      },
      SeriesBook1: {
        ...this.genericArc(this.getNextId(), seedBooks.SeriesBook1),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS |
          FLAGS.BOOK_BUB,
        requiredReviewCount: 3,
        seriesLimitType: 2
      },
      SeriesBook2: {
        ...this.genericArc(this.getNextId(), seedBooks.SeriesBook2),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS |
          FLAGS.BOOK_BUB,
        requiredReviewCount: 3,
        seriesLimitType: 2
      },
      BlockedFromTeamAndReported: {
        ...this.genericArc(this.getNextId(), seedBooks.BlockedFromTeamAndReported, seedArcTeams.PlaceholderTeamOne),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS |
          FLAGS.BOOK_BUB,
        requiredReviewCount: 3,
        userClaims: [
          {
            userId: seedUsers.BadBoy.id,
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 5,
                review: 'A 5 star review from Bad Boy: ' + this.faker.lorem.paragraph().substring(0, 150),
                privateFeedback: 'Bad Boy feedback'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.AMAZON,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                },
                {
                  ...this.baseSiteDto,
                  site: FLAGS.GOOGLE,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                },
                {
                  ...this.baseSiteDto,
                  site: FLAGS.SMASH_WORDS,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                  link: ''
                }
              ]
            },
            report: {
              type: bsConstants.ARC_TEAMS.MEMBERS.STATUS_REASON_TYPE.HARASSMENT,
              reason: 'Why, oh why?',
              reportToStaff: true
            }
          }
        ]
      },
      TooManyCancellationsReported: {
        ...this.genericArc(this.getNextId(), seedBooks.TooManyCancellationsReported),
        userClaims: [
          {
            userId: seedUsers.BadBoy.id,
            cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.BAD_FIT,
            cancellationReason: this.faker.lorem.paragraph().substring(0, 150),
            additionalInformation: this.faker.lorem.paragraph().substring(0, 150),
            report: {
              type: bsConstants.ARC_TEAMS.MEMBERS.STATUS_REASON_TYPE.CANCELLED,
              reason: this.faker.lorem.paragraph().substring(0, 150),
              reportToStaff: true
            }
          }
        ]
      },
      NotificationAuthor: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationAuthor, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(1),
        reviewFlags: FLAGS.AMAZON | FLAGS.BOOK_BUB | FLAGS.SMASH_WORDS,
        stopDownloadDate: dateMinusDays(2),
        dueDate: dateAddDays(2),
        skipAudioBookCreation: true
      },
      NotificationAuthorOtherLanguage: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationAuthorOtherLanguage, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(1),
        reviewFlags: FLAGS.AMAZON | FLAGS.BOOK_BUB | FLAGS.SMASH_WORDS,
        stopDownloadDate: dateMinusDays(2),
        dueDate: dateAddDays(2),
        skipAudioBookCreation: true
      },
      NotificationAuthorTimeToPublish: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationAuthorTimeToPublish, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(10),
        reviewFlags: FLAGS.AMAZON | FLAGS.BOOK_BUB | FLAGS.SMASH_WORDS,
        stopDownloadDate: dateMinusDays(1),
        dueDate: dateMinusDays(0),
        skipAudioBookCreation: true
      },
      NotificationDelayExpiring: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationDelayExpiring, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(30),
        stopDownloadDate: dateMinusDays(20),
        dueDate: dateMinusDays(13),
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.NotificationReviewer.id,
            skipEligibleCheck: true,
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 4,
                review: 'Notification Expiring Review',
                privateFeedback: 'Notification Expiring Review Private Feedback'
              },
              sites: [{
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED,
                statusDate: dateMinusDays(6)
              }]
            }
          }
        ]
      },
      NotificationDelayExpired: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationDelayExpired, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(30),
        stopDownloadDate: dateMinusDays(20),
        dueDate: dateMinusDays(13),
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.NotificationReviewer.id,
            skipEligibleCheck: true,
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 4,
                review: 'Notification Expired Review',
                privateFeedback: 'Notification Expired Review Private Feedback'
              },
              sites: [{
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED,
                statusDate: dateMinusDays(14)
              }]
            }
          }
        ]
      },
      NotificationReadyToReviewEverywhere: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationReadyToReviewEverywhere, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(10),
        stopDownloadDate: dateMinusDays(1),
        dueDate: dateMinusDays(0),
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.NotificationReviewer.id,
            skipEligibleCheck: true
          }
        ]
      },
      NotificationDueSoon: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationDueSoon, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(10),
        stopDownloadDate: dateMinusDays(0),
        dueDate: dateAddDays(2),
        skipAudioBookCreation: true,
        // don't add review, otherwise notification won't generate
        userClaims: [
          {
            userId: seedUsers.NotificationReviewer.id,
            createdDate: dateMinusDays(10),
            skipEligibleCheck: true
          }
        ]
      },
      // everything is same as for previous arc except the due date
      NotificationDueSoonShouldNotGenerate: {
        ...this.genericArc(this.getNextId(), seedBooks.NotificationDueSoonShouldNotGenerate, seedArcTeams.TeamNotify),
        liveDate: dateMinusDays(10),
        stopDownloadDate: dateMinusDays(0),
        dueDate: dateAddDays(3),
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.NotificationReviewer.id,
            createdDate: dateMinusDays(10),
            skipEligibleCheck: true
          }
        ]
      },
      NotificationDeletedArc: {
        ...this.genericArc(this.getNextId(), seedBooks.DeletedBook, seedArcTeams.DeletedTeam),
        deleted: true,
        userClaims: [
          {
            userId: seedUsers.NotificationReviewer.id
          }
        ]
      },
      HasReportedPirate: {
        ...this.genericArc(this.getNextId(), seedBooks.HasReportedPirate),
        skipAudioBookCreation: true,
        reportPirateArgs: {
          bookId: seedBooks.HasReportedPirate.id + '',
          link: 'https://www.test.com',
          fileName: 'test'
        },
        pirateId: seedUsers.Pirate.pirateId
      },
      HasReviews: {
        ...this.genericArc(this.getNextId(), seedBooks.HasReviews),
        skipAudioBookCreation: true,
        liveDate: dateMinusDays(10),
        stopDownloadDate: dateMinusDays(1),
        dueDate: dateMinusDays(0),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOGLE |
          FLAGS.BOOK_BUB |
          FLAGS.GOOD_READS,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS,
        requiredReviewCount: 2,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(7),
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.SMASH_WORDS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }, {
          userId: seedUsers.Thrawn.id,
          createdDate: dateMinusDays(6),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(6),
              rating: 4,
              review: 'A 4 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.REJECTED,
                statusReason: 'Amazon rejected review'
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.SMASH_WORDS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              }
            ]
          }
        }, {
          userId: seedUsers.StanRizzo.id,
          createdDate: dateMinusDays(5),
          state: bsConstants.ARCS.CLAIMS.STATE.NEW_ACTIVITY,
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(5),
              rating: 3,
              review: 'A 3 star review: KEEP THE TEXT CONSTANT SO TESTS CAN VERIFY IT',
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.SMASH_WORDS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              }
            ]
          }
        }, {
          userId: seedUsers.HanSolo.id,
          createdDate: dateMinusDays(4),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(4),
              rating: 2,
              review: 'A 2 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.SMASH_WORDS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              }
            ]
          }
        }, {
          userId: seedUsers.DarthVadar.id,
          createdDate: dateMinusDays(3),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(3),
              rating: 1,
              review: 'A 1 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOGLE,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.SMASH_WORDS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE,
                link: ''
              }
            ]
          }
        }, {
          userId: seedUsers.RandomUsers[0].id
        }, {
          userId: seedUsers.RandomUsers[1].id,
          cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.OTHER,
          cancellationReason: this.faker.lorem.paragraph().substring(0, 150),
          additionalInformation: this.faker.lorem.paragraph().substring(0, 150)
        }]
      },
      OptionalStores: {
        ...this.genericArc(this.getNextId(), seedBooks.OptionalStores),
        skipAudioBookCreation: true,
        reviewFlags: FLAGS.NONE,
        optionalReviewFlags: FLAGS.AMAZON
      },
      Scheduled: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.Scheduled.id,
        userId: seedArcTeams.TimothyZahnFans.userId,
        arcTeamId: seedArcTeams.TimothyZahnFans.id,
        specialInformation: 'Scheduled Arc',
        reviewFlags: FLAGS.AMAZON,
        optionalReviewFlags: FLAGS.BARNES | FLAGS.KOBO,
        requiredReviewCount: 2
      },
      ComplexOverdueAmazonDelayedOneComplete: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplexOverdueAmazonDelayedOneComplete),
        specialInformation: 'Complex Overdue ARC',
        reviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS,
        requiredReviewCount: 2,
        liveDate: dateMinusDays(21),
        stopDownloadDate: dateMinusDays(5),
        dueDate: dateMinusDays(4),
        userClaims: [{
          userId: seedUsers.ComplexOverdue.id,
          createdDate: dateMinusDays(15),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(7),
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              { ...this.baseSiteDto, site: FLAGS.AMAZON, status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED },
              { ...this.baseSiteDto, site: FLAGS.GOOD_READS, status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE }
            ]
          }
        }]
      },
      ComplexOverdueArc5Days: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplexOverdueBook5Days),
        specialInformation: 'Complex Overdue ARC by 5 days',
        reviewFlags: FLAGS.GOOD_READS,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(21),
        stopDownloadDate: dateMinusDays(10),
        dueDate: dateMinusDays(9),
        userClaims: [{
          userId: seedUsers.ComplexOverdue.id,
          createdDate: dateMinusDays(15),
          skipEligibleCheck: true
        }, {
          userId: seedUsers.ClaimNonMemberTooManyDue.id,
          createdDate: dateMinusDays(11),
          skipEligibleCheck: true
        }]
      },
      ComplexOverdueArc4Days: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplexOverdueBook4Days),
        specialInformation: 'Complex Overdue ARC by 4 days',
        reviewFlags: FLAGS.GOOGLE,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(21),
        stopDownloadDate: dateMinusDays(7),
        dueDate: dateMinusDays(6),
        userClaims: [{
          userId: seedUsers.ComplexOverdue.id,
          skipEligibleCheck: true,
          createdDate: dateMinusDays(15),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(7),
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            }
          }
        }, {
          userId: seedUsers.ClaimNonMemberTooManyDue.id,
          createdDate: dateMinusDays(20),
          skipEligibleCheck: true
        }]
      },
      ComplexOverdueArcToday: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplexOverdueBookToday),
        specialInformation: 'Complex Overdue Due Today',
        reviewFlags: FLAGS.GOOD_READS,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(21),
        stopDownloadDate: startOfDay(new Date()),
        dueDate: startOfDay(new Date()),
        userClaims: [{
          userId: seedUsers.ComplexOverdue.id,
          createdDate: dateMinusDays(5),
          skipEligibleCheck: true
        }]
      },
      ComplexOverdueArcYesterday: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplexOverdueBookYesterday),
        specialInformation: 'Complex Overdue Due Yesterday',
        reviewFlags: FLAGS.GOOD_READS,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(21),
        stopDownloadDate: dateMinusDays(2),
        dueDate: dateMinusDays(1),
        userClaims: [{
          userId: seedUsers.ComplexOverdue.id,
          createdDate: dateMinusDays(7),
          skipEligibleCheck: true
        }]
      },
      ComplexOverdueArcInFuture: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplexOverdueBookInFuture),
        specialInformation: 'Complex Overdue Due In Future',
        reviewFlags: FLAGS.GOOD_READS,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(21),
        stopDownloadDate: dateMinusDays(12),
        dueDate: dateAddDays(11),
        userClaims: [{
          userId: seedUsers.ComplexOverdue.id,
          createdDate: dateMinusDays(7),
          skipEligibleCheck: true
        }]
      },
      ComplexOverdueArc4DaysAwaitingPublish: {
        ...this.genericArc(this.getNextId(), seedBooks.ComplexOverdueBook4DaysAwaitingPublish),
        specialInformation: 'Complex Overdue ARC by 4 days but awaiting publish',
        reviewFlags: FLAGS.KOBO,
        requiredReviewCount: 1,
        liveDate: dateMinusDays(21),
        stopDownloadDate: dateMinusDays(4),
        dueDate: dateMinusDays(3),
        userClaims: [{
          userId: seedUsers.ComplexOverdue.id,
          skipEligibleCheck: true,
          createdDate: dateMinusDays(15),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(7),
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            }
          }
        }]
      },
      AllSitesNoLinks: {
        ...this.genericArc(this.getNextId(), seedBooks.AllSitesNoLinks),
        reviewFlags:
        FLAGS.AMAZON,
        requiredReviewCount: 2,
        optionalReviewFlags:
          FLAGS.SMASH_WORDS |
          FLAGS.KOBO |
          FLAGS.BOOK_BUB |
          FLAGS.GOOD_READS |
          FLAGS.BARNES |
          FLAGS.GOOGLE |
          FLAGS.ITUNES
      },
      OutstandingReviewWithClaim: {
        ...this.genericArc(this.getNextId(), seedBooks.OutstandingReviewWithClaim),
        arcTeamId: seedArcTeams.OutstandingReviewTeam.id,
        specialInformation: 'Outstanding review with claimed arc',
        liveDate: dateMinusDays(21),
        stopDownloadDate: dateMinusDays(4),
        dueDate: dateMinusDays(1),
        skipAudioBookCreation: true,
        userClaims: [{
          userId: seedUsers.ClaimNonMemberOutstandingReviewForThisTeam.id,
          createdDate: dateMinusDays(15)
        }, {
          // delayed review on other claim is required, so we can check the bug is fixed
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(9),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              }
            ]
          }
        }]
      },
      OutstandingReviewToBeClaimed: {
        ...this.genericArc(this.getNextId(), seedBooks.OutstandingReviewToBeClaimed),
        arcTeamId: seedArcTeams.OutstandingReviewTeam.id,
        specialInformation: 'Outstanding review to be claimed arc',
        liveDate: dateMinusDays(0),
        stopDownloadDate: dateAddDays(7),
        dueDate: dateAddDays(11),
        skipAudioBookCreation: true
      },
      AuthorsDirectAndKoboAudiobook: {
        ...this.genericArc(this.getNextId(), seedBooks.AuthorsDirectAndKoboAudiobook),
        specialInformation: 'Authors Direct and Kobo audiobook',
        liveDate: dateMinusDays(0),
        stopDownloadDate: dateAddDays(7),
        dueDate: dateAddDays(11),
        skipEbookCreation: true,
        isAuthorsDirectOrKoboAudiobook: true,
        reviewFlags: FLAGS.AUDIO_KOBO | FLAGS.AUDIO_BOOK_BUB,
        arcType: bsConstants.ARCS.TYPE.AUDIO_BOOK
      },
      RequiredSiteIsDelayedWithOptionalSitesDone: {
        ...this.genericArc(this.getNextId(), seedBooks.RequiredSiteIsDelayedWithOptionalSitesDone),
        skipAudioBookCreation: true,
        liveDate: dateMinusDays(10),
        stopDownloadDate: dateMinusDays(1),
        dueDate: dateMinusDays(0),
        reviewFlags:
          FLAGS.AMAZON |
          FLAGS.GOOD_READS,
        optionalReviewFlags:
          FLAGS.GOOGLE |
          FLAGS.KOBO |
          FLAGS.BOOK_BUB |
          FLAGS.ITUNES |
          FLAGS.BARNES,
        requiredReviewCount: 2,
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(7),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(7),
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.ITUNES,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BARNES,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      TwoArcsRunningEBookAndAudioBookE: {
        ...this.genericArc(this.getNextId(), seedBooks.BookToRunAudioAndEBook, seedArcTeams.EbookAndAudioBook),
        liveDate: dateMinusDays(30),
        stopDownloadDate: dateMinusDays(20),
        neverEndingPrivate: false,
        dueDate: dateMinusDays(14),
        skipAudioBookCreation: true,
        userClaims: [{
          userId: seedUsers.EbookAndAudioBookReviewer.id,
          createdDate: dateMinusDays(25),
          review: {
            dto: {
              ...this.baseReviewDto,
              createdDate: dateMinusDays(22),
              rating: 5,
              review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
              privateFeedback: 'Some private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      TwoArcsRunningEBookAndAudioBookA: {
        ...this.genericArc(this.getNextId(), seedBooks.BookToRunAudioAndEBook, seedArcTeams.EbookAndAudioBook),
        liveDate: dateMinusDays(14),
        stopDownloadDate: dateAddDays(14),
        neverEndingPrivate: true,
        dueDate: dateAddDays(20),
        skipEbookCreation: true,
        arcType: bsConstants.ARCS.TYPE.AUDIO_BOOK
      },
      DueForTooLongArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.DueForTooLong.id,
        userId: seedArcTeams.PlaceholderTeamOne.userId,
        arcTeamId: seedArcTeams.PlaceholderTeamOne.id,
        specialInformation: 'DueForTooLong ARC',
        reviewFlags: FLAGS.AMAZON,
        liveDate: dateMinusDays(180),
        stopDownloadDate: dateMinusDays(160),
        dueDate: dateMinusDays(155),
        neverEndingPrivate: false,
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.ClaimNonMemberDueTooLong.id,
            createdDate: dateMinusDays(160)
          },
          {
            userId: seedUsers.ArcTeamRestrictionsReader.id,
            createdDate: dateMinusDays(160)
          }
        ]
      },
      EndlessArcStarted60DaysAgo: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BookForEndlessArc.id,
        neverEndingPrivate: true,
        liveDate: dateMinusDays(60),
        userId: seedUsers.Me.id,
        arcTeamId: seedArcTeams.StanRegular.id,
        skipAudioBookCreation: true,
      },
      EndlessAudibleExclusiveArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.BookToRunAudioAndEBook.id,
        userId: seedArcTeams.EbookAndAudioBook.userId,
        arcTeamId: seedArcTeams.EbookAndAudioBook.id,
        specialInformation: 'Endless Audible-Exclusive Audiobook ARC',
        arcType: bsConstants.ARCS.TYPE.AUDIO_BOOK,
        skipEbookCreation: true,
        neverEndingPrivate: true,
        reviewFlags: FLAGS.AUDIBLE,
        optionalReviewFlags: FLAGS.AUDIO_BOOK_BUB | FLAGS.AUDIO_GOODREADS,
        requiredReviewCount: 2,
        usDownloadCodes: 'US-CODE-1,US-CODE-2,US-CODE-3',
        ukDownloadCodes: 'UK-CODE-1,UK-CODE-2,UK-CODE-3',
        liveDate: dateMinusDays(1),
        dueDate: dateAddDays(30)
      },
      PendingPublishingWithReleaseDateInFutureArc: {
        ...this.genericArc(this.getNextId(), seedBooks.PendingPublishingWithReleaseDateInFuture),
        liveDate: dateAddDays(-10),
        stopDownloadDate: dateAddDays(2),
        dueDate: dateAddDays(6),
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.YourArcs.id,
            createdDate: dateMinusDays(7),
            review: {
              dto: {
                ...this.baseReviewDto,
                createdDate: dateMinusDays(2),
                rating: 5,
                review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
                privateFeedback: 'Some private feedback'
              },
            }
          }
        ]
      },
      PendingPublishingWithReleaseDateInFutureButHasPrintAsinArc: {
        ...this.genericArc(this.getNextId(), seedBooks.PendingPublishingWithReleaseDateInFutureButHasPrintAsin),
        liveDate: dateAddDays(-10),
        stopDownloadDate: dateAddDays(2),
        dueDate: dateAddDays(6),
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.YourArcs.id,
            createdDate: dateMinusDays(7),
            review: {
              dto: {
                ...this.baseReviewDto,
                createdDate: dateMinusDays(2),
                rating: 5,
                review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
                privateFeedback: 'Some private feedback'
              },
            }
          }
        ]
      },
      PendingPublishingWithReleaseDateInFutureWithOptionalSitesArc: {
        ...this.genericArc(this.getNextId(), seedBooks.PendingPublishingWithReleaseDateInFutureWithOptionalSites),
        liveDate: dateAddDays(-10),
        stopDownloadDate: dateAddDays(2),
        dueDate: dateAddDays(6),
        optionalReviewFlags: FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.YourArcs.id,
            createdDate: dateMinusDays(7),
            review: {
              dto: {
                ...this.baseReviewDto,
                createdDate: dateMinusDays(2),
                rating: 5,
                review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
                privateFeedback: 'Some private feedback'
              },
            }
          }
        ]
      },
      PendingPublishingWithReleaseDateInFutureWithRequiredSitesArc: {
        ...this.genericArc(this.getNextId(), seedBooks.PendingPublishingWithReleaseDateInFutureWithRequiredSites),
        liveDate: dateAddDays(-10),
        stopDownloadDate: dateAddDays(2),
        dueDate: dateAddDays(6),
        reviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        requiredReviewCount: 3,
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.YourArcs.id,
            createdDate: dateMinusDays(7),
            review: {
              dto: {
                ...this.baseReviewDto,
                createdDate: dateMinusDays(2),
                rating: 5,
                review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
                privateFeedback: 'Some private feedback'
              },
            }
          }
        ]
      },
      PendingPublishingWithReleaseDateInPastAndHasPrintAsinArc: {
        ...this.genericArc(this.getNextId(), seedBooks.PendingPublishingWithReleaseDateInPastAndHasPrintAsin),
        liveDate: dateAddDays(-10),
        stopDownloadDate: dateAddDays(0),
        dueDate: dateAddDays(2),
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        skipAudioBookCreation: true,
        userClaims: [
          {
            userId: seedUsers.YourArcs.id,
            createdDate: dateMinusDays(7),
            review: {
              dto: {
                ...this.baseReviewDto,
                createdDate: dateMinusDays(2),
                rating: 5,
                review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
                privateFeedback: 'Some private feedback'
              },
            }
          }
        ]
      },
      ArcForSameBook: {
        ...this.genericArc(this.getNextId(), seedBooks.ArcForSameBook, seedArcTeams.ArcForSameBook),
        liveDate: dateAddDays(-7),
        stopDownloadDate: dateAddDays(14),
        dueDate: dateAddDays(21),
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        specialInformation: 'Arc for same book - normal arc',
        skipAudioBookCreation: true
      },
      ScheduledArcForSameBook: {
        ...this.genericArc(this.getNextId(), seedBooks.ArcForSameBook, seedArcTeams.ArcForSameBook),
        liveDate: dateAddDays(14),
        stopDownloadDate: dateAddDays(21),
        dueDate: dateAddDays(28),
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        specialInformation: 'Arc for same book - scheduled arc',
        skipAudioBookCreation: true
      },
      DuplicateArcForSameBook: {
        ...this.genericArc(this.getNextId(), seedBooks.ArcForSameBook, seedArcTeams.ArcForSameBook),
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        // don't set any dates
        liveDate: void 0,
        stopDownloadDate: void 0,
        dueDate: void 0,
        skipAudioBookCreation: true
      },
      ArcTeamRestrictions: {
        ...this.genericArc(this.getNextId(), seedBooks.ArcTeamRestrictions, seedArcTeams.ArcTeamRestrictions),
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        siteWideParticipationPreventionType: bsConstants.ARCS.SITE_WIDE_PARTICIPATION_PREVENTION_TYPE.LIMITED,
        liveDate: dateAddDays(0),
        stopDownloadDate: dateAddDays(14),
        dueDate: dateAddDays(21),
        skipAudioBookCreation: true
      },
      TooManyWithdrawals1: {
        ...this.genericArc(this.getNextId(), seedBooks.TooManyWithdrawals1, seedArcTeams.TooManyWithdrawals),
        userClaims: [{
          userId: seedUsers.TooManyWithdrawals.id,
          createdDate: dateMinusDays(7),
          cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.PERSONAL,
          cancellationReason: 'Too Many Withdrawals 1'
        }]
      },
      TooManyWithdrawals2: {
        ...this.genericArc(this.getNextId(), seedBooks.TooManyWithdrawals2, seedArcTeams.TooManyWithdrawals),
        userClaims: [{
          userId: seedUsers.TooManyWithdrawals.id,
          createdDate: dateMinusDays(7),
          cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.PERSONAL,
          cancellationReason: 'Too Many Withdrawals 2'
        }]
      },
      TooManyWithdrawals3: {
        ...this.genericArc(this.getNextId(), seedBooks.TooManyWithdrawals3, seedArcTeams.TooManyWithdrawals),
        userClaims: [{
          userId: seedUsers.TooManyWithdrawals.id,
          createdDate: dateMinusDays(7),
          cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.PERSONAL,
          cancellationReason: 'Too Many Withdrawals 3'
        }]
      },
      TooManyWithdrawals4: {
        ...this.genericArc(this.getNextId(), seedBooks.TooManyWithdrawals4, seedArcTeams.TooManyWithdrawals),
        userClaims: [{
          userId: seedUsers.TooManyWithdrawals.id,
          createdDate: dateMinusDays(7),
          cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.PERSONAL,
          cancellationReason: 'Too Many Withdrawals 4'
        }]
      },
      TooManyWithdrawals5: {
        ...this.genericArc(this.getNextId(), seedBooks.TooManyWithdrawals5, seedArcTeams.TooManyWithdrawals),
        userClaims: [{
          userId: seedUsers.TooManyWithdrawals.id,
          createdDate: dateMinusDays(7),
          cancellationType: bsConstants.ARCS.CLAIMS.CANCELLATION_TYPE.PERSONAL,
          cancellationReason: 'Too Many Withdrawals 5'
        }]
      },
      TooManyWithdrawalsReviewOutstanding: {
        ...this.genericArc(this.getNextId(), seedBooks.TooManyWithdrawalsReviewOutstanding, seedArcTeams.TooManyWithdrawals),
        userClaims: [{
          userId: seedUsers.TooManyWithdrawals.id,
          createdDate: dateMinusDays(7),
          skipEligibleCheck: true
        }]
      },
      AmazonDelayedRequiredArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.AmazonDelayedRequiredBook.id,
        userId: seedUsers.Me.id,
        arcTeamId: seedArcTeams.AmazonDelayedRequiredTeam.id,
        specialInformation: 'Amazon delayed required arc',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        skipAudioBookCreation: true,
        liveDate: newDate(),
        stopDownloadDate: dateMinusDays(40),
        dueDate: dateMinusDays(45),
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(40),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 4,
              review: 'Required Amazon site is delayed',
              privateFeedback: 'Amazon delayed required private feedback'
            },
            sites: [{
              ...this.baseSiteDto,
              site: FLAGS.AMAZON,
              status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
            }]
          }
        }]
      },
      AmazonDelayedMultiArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.AmazonDelayedMultiBook.id,
        userId: seedUsers.Me.id,
        arcTeamId: seedArcTeams.AmazonDelayedMultiTeam.id,
        specialInformation: 'Amazon delayed multi-site arc',
        reviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS,
        optionalReviewFlags: FLAGS.BOOK_BUB,
        requiredReviewCount: 2,
        skipAudioBookCreation: true,
        liveDate: newDate(),
        stopDownloadDate: dateMinusDays(50),
        dueDate: dateMinusDays(55),
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(50),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 5,
              review: 'Amazon delayed but other sites complete',
              privateFeedback: 'Amazon delayed multi private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      AmazonRejectedRequiredArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.AmazonRejectedRequiredBook.id,
        userId: seedUsers.Me.id,
        arcTeamId: seedArcTeams.AmazonRejectedRequiredTeam.id,
        specialInformation: 'Amazon rejected required arc',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        skipAudioBookCreation: true,
        liveDate: newDate(),
        stopDownloadDate: dateMinusDays(42),
        dueDate: dateMinusDays(48),
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(32),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 3,
              review: 'Required Amazon site is rejected',
              privateFeedback: 'Amazon rejected required private feedback'
            },
            sites: [{
              ...this.baseSiteDto,
              site: FLAGS.AMAZON,
              status: bsConstants.ARCS.REVIEWS.SITE.STATUS.REJECTED,
              statusReason: 'Amazon rejected the submission'
            }]
          }
        }]
      },
      AmazonRejectedMultiArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.AmazonRejectedMultiBook.id,
        userId: seedUsers.Me.id,
        arcTeamId: seedArcTeams.AmazonRejectedMultiTeam.id,
        specialInformation: 'Amazon rejected multi-site arc',
        reviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS,
        optionalReviewFlags: FLAGS.BOOK_BUB,
        requiredReviewCount: 2,
        skipAudioBookCreation: true,
        liveDate: newDate(),
        stopDownloadDate: dateMinusDays(52),
        dueDate: dateMinusDays(60),
        userClaims: [{
          userId: seedUsers.YourArcs.id,
          createdDate: dateMinusDays(52),
          review: {
            dto: {
              ...this.baseReviewDto,
              rating: 4,
              review: 'Amazon rejected but other sites complete',
              privateFeedback: 'Amazon rejected multi private feedback'
            },
            sites: [
              {
                ...this.baseSiteDto,
                site: FLAGS.AMAZON,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.REJECTED,
                statusReason: 'Amazon rejected the submission'
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.GOOD_READS,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              },
              {
                ...this.baseSiteDto,
                site: FLAGS.BOOK_BUB,
                status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
              }
            ]
          }
        }]
      },
      NoCategoriesArc: {
        ...this.genericArc(this.getNextId(), seedBooks.NoCategoriesBook, seedArcTeams.StanRegular),
        skipAudioBookCreation: true,
        skipBookDraftCheck: true
      },
      LegacyBookFieldsArc: {
        ...this.genericArc(this.getNextId(), seedBooks.LegacyBookFieldsBook, seedArcTeams.StanRegular),
        skipAudioBookCreation: true,
        skipBookDraftCheck: true
      },
      AppleCodesAllClaimed: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.AppleCodesAllClaimedBook.id,
        // Me's team, because the book is one of Me's - getGeneralTestBook hardcodes that owner.
        // An ARC whose creator is not its book's author is a shape nothing else in the seed has,
        // and it hid both apple ARCs from the author ARC list, which scopes on a.userId.
        userId: seedArcTeams.AppleCodesTeam.userId,
        arcTeamId: seedArcTeams.AppleCodesTeam.id,
        specialInformation: 'ARC with Apple Books + Amazon required but all Apple codes claimed',
        reviewFlags: FLAGS.AMAZON | FLAGS.ITUNES,
        requiredReviewCount: 2,
        skipAudioBookCreation: true,
        liveDate: dateAddDays(0),
        stopDownloadDate: dateAddDays(30),
        dueDate: dateAddDays(30),
        appleBooksCodesToClaim: ['appleNoAvail1', 'appleNoAvail2', 'appleNoAvail3'],
        userClaims: [
          { userId: seedUsers.HanSolo.id },
          { userId: seedUsers.MaraJade.id },
          { userId: seedUsers.ReviewerNotClaimedBefore.id }
        ]
      },
      AppleCodesAvailable: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.AppleCodesAvailableBook.id,
        userId: seedArcTeams.AppleCodesTeam.userId,
        arcTeamId: seedArcTeams.AppleCodesTeam.id,
        specialInformation: 'ARC with Apple Books + Amazon required and Apple codes available',
        reviewFlags: FLAGS.AMAZON | FLAGS.ITUNES,
        requiredReviewCount: 2,
        skipAudioBookCreation: true,
        liveDate: dateAddDays(0),
        stopDownloadDate: dateAddDays(30),
        dueDate: dateAddDays(30),
        // One reader holding a DATED code, so the terms panel Apple require can be exercised with an
        // expiry in it (sc-28048). Two of the three codes are left free, which is what keeps this
        // arc's "Apple Books is still offered" case intact.
        appleBooksCodesToClaim: ['appleAvail1'],
        userClaims: [
          {
            userId: seedUsers.LukeSkywalker.id,
            skipEligibleCheck: true,
            // The store panels only come up once the Booksprout review is written, so the reader
            // needs one before the Apple Books step - and its terms panel - is reachable.
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 5,
                review: 'A dated Apple Books code was used to read this one.',
                privateFeedback: 'Apple codes available private feedback'
              }
            }
          }
        ]
      },
      ReviewTranslationArc: {
        ...this.base,
        id: this.getNextId(),
        bookId: seedBooks.ReviewTranslationBook.id,
        userId: seedArcTeams.ReviewTranslationTeam.userId,
        arcTeamId: seedArcTeams.ReviewTranslationTeam.id,
        specialInformation: 'ARC for testing translated reviews',
        reviewFlags: FLAGS.AMAZON,
        requiredReviewCount: 1,
        skipAudioBookCreation: true,
        liveDate: dateMinusDays(3),
        stopDownloadDate: dateAddDays(21),
        dueDate: dateAddDays(28),
        userClaims: [
          {
            userId: seedUsers.RandomUsers[0].id,
            createdDate: dateMinusDays(2),
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 5,
                title: 'Tolle Geschichte',
                review: 'Dieses Buch war fesselnd und emotional. Ich konnte es kaum aus der Hand legen.',
                privateFeedback: 'Mehr Szenen mit den Nebenfiguren waeren schoen.'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.AMAZON,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                }
              ]
            }
          },
          {
            userId: seedUsers.RandomUsers[1].id,
            createdDate: dateMinusDays(2),
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 4,
                title: 'Lecture captivante',
                review: 'Ce livre etait captivant et emouvant. Je l ai lu d une traite.',
                privateFeedback: 'Le debut etait un peu lent, mais la suite compense largement.'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.AMAZON,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                }
              ]
            }
          },
          {
            userId: seedUsers.RandomUsers[2].id,
            createdDate: dateMinusDays(1),
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 5,
                title: 'Leitura envolvente',
                review: 'Este livro foi envolvente e emocionante. Nao consegui parar de ler.',
                privateFeedback: 'Eu adoraria ver mais desse universo em outro livro.'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.AMAZON,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                }
              ]
            }
          },
          {
            userId: seedUsers.RandomUsers[3].id,
            createdDate: dateMinusDays(1),
            review: {
              dto: {
                ...this.baseReviewDto,
                rating: 5,
                title: 'Labai stiprus ir jautrus pasakojimas',
                review: longLithuanianReview,
                privateFeedback: 'Ilgesnė lietuviška apžvalga puikiai tinka vertimo ir formatavimo testui.'
              },
              sites: [
                {
                  ...this.baseSiteDto,
                  site: FLAGS.AMAZON,
                  status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
                }
              ]
            }
          }
        ]
      },
      // sc-26587: the two reviewer "saved book" email scenarios. SavedArcReader SAVES both
      // ARCs (userSaves) and claims neither, so the notifiers select them.
      SavedArcEndingSoon: {
        ...this.genericArc(this.getNextId(), seedBooks.SavedArcEndingSoonBook, seedArcTeams.SavedArcTeam),
        specialInformation: 'Saved ARC whose campaign is ending soon',
        skipAudioBookCreation: true,
        copyCountType: bsConstants.ARCS.COPY_COUNT_TYPE.ALL, // unlimited, so it can't also trip "low copies"
        liveDate: dateMinusDays(7),
        stopDownloadDate: dateAddDays(1), // inside the 2-day ending-soon window
        dueDate: dateAddDays(8),
        userSaves: [seedUsers.SavedArcReader.id]
      },
      SavedArcLowCopies: {
        ...this.genericArc(this.getNextId(), seedBooks.SavedArcLowCopiesBook, seedArcTeams.SavedArcTeam),
        specialInformation: 'Saved ARC running low on copies',
        skipAudioBookCreation: true,
        copyCountType: bsConstants.ARCS.COPY_COUNT_TYPE.LIMITED,
        copyCount: 5,
        liveDate: dateMinusDays(7),
        stopDownloadDate: dateAddDays(30), // far out, so it can't trip the ending-soon rule
        dueDate: dateAddDays(37),
        // 3 filler claims against 5 copies => 2 remaining, inside the 1-5 "low copies" range.
        userClaims: [
          { userId: seedUsers.SavedArcFillers[0].id, createdDate: dateMinusDays(3) },
          { userId: seedUsers.SavedArcFillers[1].id, createdDate: dateMinusDays(3) },
          { userId: seedUsers.SavedArcFillers[2].id, createdDate: dateMinusDays(3) }
        ],
        userSaves: [seedUsers.SavedArcReader.id]
      },
      // sc-29038: one more campaign than the capped publisher may have live, all due and all left scheduled.
      // Whenever arcCron runs, it makes the first 10 live, puts Capped Publisher Book 11's back to draft and
      // queues a single "cannot go live" notification; cron:notifications then delivers it. The window is
      // long so a seed a few weeks old still works. TEAM_ONLY keeps the 10 out of other specs' feeds.
      CappedPublisherArcs: seedBooks.CappedPublisherBooks.map(book => ({
        ...this.genericArc(this.getNextId(), book, seedArcTeams.CappedPublisherTeam),
        discoverableType: bsConstants.ARCS.DISCOVERABLE_TYPE.TEAM_ONLY,
        skipAudioBookCreation: true,
        liveDate: dateMinusDays(1),
        stopDownloadDate: dateAddDays(55),
        dueDate: dateAddDays(62),
        holdAsScheduled: true
      }))
    }

    return {
      ...testData,
      ...this.generateAudioBookTestData(testData),
      // After the audiobook copies, so adding these moved none of the ids before them.
      ...this.getDelayedAmazonTestData(),
      RandomArcsWithClaims: this.generateRandomData()
    }
  }

  /**
   * sc-29117: one claim per case of a delayed or rejected Amazon review deciding between Due and Delayed,
   * all of them DelayedAmazonReader's. Each starts with Amazon untouched: the reviewer e2e marks it delayed
   * or rejected and checks where the api files the claim, so the move is worked out as it is for a reader,
   * not baked into the seed. Team only, on the random-ARCs team, so no public list or author list counts them.
   */
  getDelayedAmazonTestData (): Pick<SeedArcs, 'DelayedAmazonLastReview' | 'DelayedAmazonTwoOwed' | 'DelayedAmazonExempt' | 'DelayedAmazonRequired' | 'DelayedAmazonOptional' | 'DelayedAmazonRejected' | 'DelayedAmazonRejectedLastReview'> {
    const delayedAmazonArc = (
      book: SeedBook,
      flags: Pick<SeedArc, 'reviewFlags' | 'optionalReviewFlags' | 'requiredReviewCount'>,
      sites: SeedArcReview['sites'] = []
    ): SeedArc => ({
      ...this.genericArc(this.getNextId(), book, seedArcTeams.TeamForRandomArcs),
      ...flags,
      discoverableType: bsConstants.ARCS.DISCOVERABLE_TYPE.TEAM_ONLY,
      skipAudioBookCreation: true,
      userClaims: [{
        userId: seedUsers.DelayedAmazonReader.id,
        createdDate: dateMinusDays(7),
        // Seven claims under review on one reader trip the due-review limits of an ordinary claim.
        skipEligibleCheck: true,
        review: {
          dto: {
            ...this.baseReviewDto,
            rating: 4,
            // The api refuses the same review text from one reader twice within two weeks.
            review: `A 4 star review of ${book.title}, posted on Booksprout before any store.`,
            privateFeedback: 'Some private feedback'
          },
          sites
        }
      }]
    })

    return {
      // Two of three stores wanted, Goodreads posted to a book with no Goodreads link, as in the support
      // case behind the story. Delayed, Amazon becomes the last review needed: the claim moves to Delayed.
      DelayedAmazonLastReview: delayedAmazonArc(seedBooks.DelayedAmazonLastReviewBook, {
        reviewFlags: 0,
        optionalReviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        requiredReviewCount: 2
      }, [
        { ...this.baseSiteDto, site: FLAGS.GOOD_READS, status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE }
      ]),
      // Three wanted: with Amazon delayed, BookBub is still owed, so the claim stays Due.
      DelayedAmazonTwoOwed: delayedAmazonArc(seedBooks.DelayedAmazonTwoOwedBook, {
        reviewFlags: 0,
        optionalReviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        requiredReviewCount: 3
      }, [
        { ...this.baseSiteDto, site: FLAGS.GOOD_READS, status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE }
      ]),
      // Three wanted, Goodreads posted and BookBub excused by the author: Amazon is the last one left.
      DelayedAmazonExempt: delayedAmazonArc(seedBooks.DelayedAmazonExemptBook, {
        reviewFlags: 0,
        optionalReviewFlags: FLAGS.AMAZON | FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        requiredReviewCount: 3
      }, [
        { ...this.baseSiteDto, site: FLAGS.GOOD_READS, status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE },
        { ...this.baseSiteDto, site: FLAGS.BOOK_BUB, status: bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE, link: '', exempt: true }
      ]),
      // Amazon required plus one of Goodreads or BookBub: delaying Amazon leaves that review to post.
      DelayedAmazonRequired: delayedAmazonArc(seedBooks.DelayedAmazonRequiredBook, {
        reviewFlags: FLAGS.AMAZON,
        optionalReviewFlags: FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        requiredReviewCount: 2
      }),
      // Goodreads required plus one of Amazon or BookBub: delaying Amazon leaves Goodreads to post.
      DelayedAmazonOptional: delayedAmazonArc(seedBooks.DelayedAmazonOptionalBook, {
        reviewFlags: FLAGS.GOOD_READS,
        optionalReviewFlags: FLAGS.AMAZON | FLAGS.BOOK_BUB,
        requiredReviewCount: 2
      }),
      // As DelayedAmazonRequired, but Amazon rejects the review: Goodreads or BookBub is still owed, so the
      // claim stays Due until it is in.
      DelayedAmazonRejected: delayedAmazonArc(seedBooks.DelayedAmazonRejectedBook, {
        reviewFlags: FLAGS.AMAZON,
        optionalReviewFlags: FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        requiredReviewCount: 2
      }),
      // The same with Goodreads posted: rejected, Amazon is the last review left, so the claim moves to
      // Delayed / Rejected.
      DelayedAmazonRejectedLastReview: delayedAmazonArc(seedBooks.DelayedAmazonRejectedLastReviewBook, {
        reviewFlags: FLAGS.AMAZON,
        optionalReviewFlags: FLAGS.GOOD_READS | FLAGS.BOOK_BUB,
        requiredReviewCount: 2
      }, [
        { ...this.baseSiteDto, site: FLAGS.GOOD_READS, status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE }
      ])
    }
  }

  generateAudioBookTestData (testData: Partial<SeedArcs>): any {
    const audioBookData: Record<string, SeedArc> = {}
    for (const seedArcKey of Object.keys(testData) as Array<keyof SeedArcs>) {
      const seedArc = testData[seedArcKey]
      if (!seedArc || Array.isArray(seedArc) || seedArc.skipAudioBookCreation) {
        continue
      }

      const audioArcKey = seedArcKey + '_Audio'
      audioBookData[audioArcKey] = {
        ...seedArc,
        id: this.getNextId(),
        arcType: bsConstants.ARCS.TYPE.AUDIO_BOOK,
        specialInformation: `AudioBook: ${seedArc.specialInformation}`
      }
    }
    return audioBookData
  }

  genericApprovedReview () {
    return {
      dto: {
        ...this.baseReviewDto,
        rating: 5,
        review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
        privateFeedback: 'Some private feedback'
      },
      sites: [
        {
          ...this.baseSiteDto,
          site: FLAGS.AMAZON,
          status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
        }
      ]
    }
  }

  genericArc (id: number, book: SeedBook, team?: SeedArcTeam): SeedArc {
    if (!team) team = seedArcTeams.StanRegular

    return {
      ...this.base,
      id: id,
      bookId: book.id,
      userId: team.userId,
      arcTeamId: team.id,
      specialInformation: book.title + ' - ARC',
      reviewFlags: FLAGS.AMAZON,
      requiredReviewCount: 1,
      liveDate: dateMinusDays(21),
      neverEndingPrivate: false,
      stopDownloadDate: dateAddDays(21),
      dueDate: dateAddDays(28),
      discoverableType: bsConstants.ARCS.DISCOVERABLE_TYPE.PUBLIC
    }
  }

  getFakeSeed (id: number): SeedArc | undefined {
    const seedArc = {
      ...this.genericArc(id, seedBooks.BookForRandomArcs, seedArcTeams.TeamForRandomArcs),
      // make sure these arcs don't affect other tests
      discoverableType: bsConstants.ARCS.DISCOVERABLE_TYPE.TEAM_ONLY,
      skipAudioBookCreation: true
    }

    seedArc.userClaims = []

    if (this.processedFakeArcs <= 3) {
      seedArc.userClaims.push({
        userId: seedUsers.ClaimNonMemberWeeklyLimitReached.id,
        createdDate: dateMinusDays(4),
        skipEligibleCheck: true
      })
    }


    if (this.processedFakeArcs <= 30) {
      seedArc.userClaims.push({
        userId: seedUsers.ClaimNonMemberWeeklyLimitReachedValued.id,
        createdDate: dateMinusDays(5),
        review: this.genericApprovedReview(),
        skipEligibleCheck: true
      }, {
        // delayed review on other claim is required, so we can check the bug is fixed
        userId: seedUsers.TopReviewerApplicationRejected.id,
        createdDate: dateMinusDays(9),
        skipEligibleCheck: true,
        review: {
          dto: {
            ...this.baseReviewDto,
            rating: 5,
            review: 'A 5 star review: ' + this.faker.lorem.paragraph().substring(0, 150),
            privateFeedback: 'Some private feedback top'
          },
          sites: [
            {
              ...this.baseSiteDto,
              site: FLAGS.AMAZON,
              status: bsConstants.ARCS.REVIEWS.SITE.STATUS.COMPLETE
            }
          ]
        }
      })
    }

    this.processedFakeArcs += 1

    return seedArc
  }
}
